import { Stack, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';
import { observable, reaction, toJS, when } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IImportColumn, IPreviewRow } from '@feathr/blackbox';
import type { Importer } from '@feathr/blackbox';
import { Alert, Button, type ButtonType, ButtonValid, Form, Spinner } from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import { EXTERNAL_ID_HELP_URL } from '../DataImportsPage.utils';
import DataImportColumnConfig from './DataImportColumnConfig';
import DataImportPreview from './DataImportPreview';
import {
  getNextUnmappedColumn,
  getPreviewMappingKey,
  getSampleValues,
  getUnreadableValues,
  matchBuiltInField,
} from './DataImportStepTwo.utils';
import ImportMappingTable from './ImportMappingTable';

interface IProps {
  disabled?: boolean;
  onPrev: () => void;
  onNext: () => void;
  importer: Importer;
  /** False when the page renders step navigation in its own action bar. */
  showActions?: boolean;
}

interface IButtonProps {
  onNext: () => void;
  importer: Importer;
  /** Button copy. Defaults to "Next"; the revamped wizard's action bar says "Continue". */
  label?: string;
  type?: ButtonType;
}

interface IPreviewState {
  rows?: IPreviewRow[];
  // Kept across remaps, which reset rows to undefined while the preview reloads.
  samples?: Record<string, string>;
  /** Each column the preview could not read, against the value it refused. Cleared on a remap, unlike `samples`. */
  unreadable?: Map<string, string>;
  /** Why the latest preview failed; cleared when a preview succeeds. */
  error?: string;
  setRows: (rows?: IPreviewRow[]) => void;
  setError: (error?: string) => void;
}

export const validateStepTwo = (importer: Importer, t: TFunction) => {
  function validateColumn(column: IImportColumn): string | undefined {
    if (!column.feathr_attr) {
      return t('{{columnName}} must map to a field.', { columnName: column.column_name });
    }
    if (!column.attr_type) {
      return t('Field for "{{columnName}}" needs a data type.', { columnName: column.column_name });
    }

    if (column?.u_key === '') {
      return t('Custom field mapped to "{{columnName}}" needs a name.', {
        columnName: column.column_name,
      });
    }
    return undefined;
  }

  const mappers: IImportColumn[] = importer.get('column_mappers');
  const hasEmail = mappers.some((m) => m.feathr_attr === 'email');
  const errors = toJS(importer.validate(['column_mappers']).errors);
  if (!hasEmail) {
    errors.push(t('Exactly one column must map to the Email field.'));
  }
  return [
    ...errors,
    ...(mappers
      .filter((column) => column.column_name !== '')
      .map(validateColumn)
      .filter((error?: string) => !!error) as string[]),
  ];
};

export const StepTwoNextButton = observer(({ onNext, importer, label, type }: IButtonProps) => {
  const { CustomFields } = useStore();
  const [fieldExists, setFieldExists] = useState<string[]>([]);
  const { t } = useTranslation();

  const validationErrors = validateStepTwo(importer, t);
  fieldExists.forEach((entry) => validationErrors.push(entry));

  const defaults = ['email'];

  useEffect(() => {
    reaction(
      () => importer.getEphemeralCustomFields(CustomFields).map((cf) => cf.get('u_key')),
      async (fieldNames) => {
        const existingFields = CustomFields.list({
          filters: {
            u_key__in: fieldNames,
            is_archived__ne: true,
            collection: 'Person',
          },
        });
        await when(() => !existingFields.isPending);
        const builtinFieldErrs = fieldNames
          .filter((fn) => defaults.includes(fn?.toLowerCase()))
          .map((fn) =>
            t('{{fieldName}} is a built-in field. Use the built in field instead.', {
              fieldName: fn,
            }),
          );
        const existingFieldErrs = existingFields.models.map((f) =>
          t(
            '{{fieldName}} is a custom field that already exists. Choose a different name or use the existing field.',
            { fieldName: f.get('u_key') },
          ),
        );
        setFieldExists([...builtinFieldErrs, ...existingFieldErrs]);
      },
      { delay: DEFAULT_DEBOUNCE_WAIT },
    );
  });

  return (
    <ButtonValid errors={validationErrors} name={'next_step'} onClick={onNext} type={type}>
      {label ?? t('Next')}
    </ButtonValid>
  );
});

function DataImportStepTwo({
  disabled = false,
  importer,
  onNext,
  onPrev,
  showActions = true,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const theme = useMantineTheme();

  const state = useLocalObservable<IPreviewState>(() => ({
    rows: undefined,
    samples: undefined,
    error: undefined,
    setRows: (rows?: IPreviewRow[]): void => {
      state.rows = rows;
      state.unreadable = rows ? getUnreadableValues(rows) : undefined;
      if (rows) {
        state.samples = getSampleValues(rows, importer.get('column_names'));
      }
    },
    setError: (error?: string): void => {
      state.error = error;
    },
  }));

  useEffect(() => {
    // Map built-in fields here too, so the first preview doesn't depend on the rows having mounted.
    if (!disabled) {
      const columns: IImportColumn[] = importer.get('column_mappers');
      const matched = columns.map(matchBuiltInField).some(Boolean);
      if (matched) {
        importer.setAttributeDirty('column_mappers');
      }
    }

    // Requests can overlap, and an earlier one may resolve last.
    let latestRequest = 0;

    const dispose = reaction(
      // completedColumnMappers alone only changes with a field, not with the column it reads.
      () => getPreviewMappingKey(importer.completedColumnMappers),
      async () => {
        const requestId = ++latestRequest;
        state.setRows(undefined);
        let previewRows: IPreviewRow[];
        let previewError: string | undefined;
        try {
          previewRows = await importer.preview(importer.completedColumnMappers);
        } catch (error) {
          // Settle on an empty preview, not a spinner that never ends, and say why it's empty.
          previewRows = [];
          previewError =
            error instanceof Error && error.message ? error.message : t('Unknown error.');
        }
        if (requestId === latestRequest) {
          state.setError(previewError);
          state.setRows(previewRows);
        }
      },
      { fireImmediately: true },
    );

    return (): void => {
      latestRequest += 1;
      dispose();
    };
    // Not on `disabled`: re-running would rebuild the reaction and send another preview.
  }, [importer, state, t]);

  function addColumn(): void {
    const name = getNextUnmappedColumn(
      importer.get('column_names') ?? [],
      importer.get('column_mappers'),
    );
    if (name === undefined) {
      return;
    }
    const obj = observable({
      column_name: name,
      feathr_attr: undefined,
      attr_type: undefined,
    } as IImportColumn);
    importer.get('column_mappers').push(obj);
  }

  const addColumnButton = (
    <Button
      disabled={
        disabled || importer.get('column_names').length === importer.get('column_mappers').length
      }
      key={'add'}
      onClick={addColumn}
    >
      {t('Add column')}
    </Button>
  );

  // The revamped step adds columns from the mapping table's footer instead.
  const legacyActions = showActions
    ? [
        <Button key={'prev'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        addColumnButton,
        <StepTwoNextButton importer={importer} key={'next'} onNext={onNext} />,
      ]
    : [addColumnButton];

  // Filter out empty columns
  const columnMappers = importer
    .get('column_mappers')
    .filter((column) => column.column_name !== '') as IObservableArray<IImportColumn>;

  const columnRows = columnMappers.map((column, index) => (
    <DataImportColumnConfig
      column={column}
      disabled={disabled}
      importer={importer}
      index={index}
      key={column.column_name}
    />
  ));

  return (
    <>
      <Form
        actions={isRevampEnabled ? undefined : legacyActions}
        description={
          // The revamped step's mapping card carries the description.
          isRevampEnabled ? undefined : (
            <Trans t={t}>
              <p>Map the columns from your spreadsheet to fields in Feathr.</p>
              <p>
                In order for your data to be properly imported into Feathr, each column of your
                spreadsheet must be mapped to a field on every person that is being imported. You
                can map each column to a field that already exists, or to a new custom field you
                create.
              </p>
              <p>
                Note that External ID and Email Address will be used to find existing records. If an
                existing record is found, its content will be overwritten by the import. For this
                reason, these fields should be unique.
              </p>
            </Trans>
          )
        }
        label={isRevampEnabled ? t('Map columns') : t('Map')}
        width={isRevampEnabled ? 'wide' : 'narrow'}
      >
        {isRevampEnabled ? (
          <Stack gap={theme.spacing['4']}>
            <Alert
              description={t(
                'A row with an External ID updates the person with that External ID, or a person with none. If the email already belongs to a person with a different External ID, this import creates a second record.',
              )}
              title={t('Important: when mapping External ID')}
              type={'info'}
            >
              <a href={EXTERNAL_ID_HELP_URL} rel={'noopener noreferrer'} target={'_blank'}>
                {t('Learn more about External ID')}
              </a>
            </Alert>
            <ImportMappingTable
              disabled={disabled}
              importer={importer}
              samples={state.samples}
              unreadable={state.unreadable}
            />
          </Stack>
        ) : (
          columnRows
        )}
      </Form>
      {state.error && (
        <Alert description={state.error} title={t("Couldn't load the preview")} type={'danger'} />
      )}
      {state.rows && columnMappers ? (
        <DataImportPreview
          columnMappers={columnMappers}
          isRevampEnabled={isRevampEnabled}
          rows={state.rows}
          tagIds={importer.get('tag_ids', [])}
        />
      ) : (
        <Spinner />
      )}
    </>
  );
}

export default observer(DataImportStepTwo);
